import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoAlertCircleOutline, IoFolderOutline, IoReturnUpForwardOutline, IoTrashOutline } from 'react-icons/io5';
import { FileKindIcon } from '../atlas/viewer/fileIcons';
import FolderSuggestion from './FolderSuggestion';
import {
  KIND_FILTERS,
  PAGE_SIZE,
  applyOverride,
  applyOverrides,
  SORT_OPTIONS,
  SOURCE_GROUP_CHIPS,
  formatBytes,
  formatDate,
  pluralize,
  processingInfo,
} from './fileLibrary';

export function VersionBadge({ file }) {
  const n = Number(file?.version_number) || 1;
  if (n <= 1) return null;
  return <span className="fl-chip fl-chip-version" title={`Version ${n}`}>v{n}</span>;
}

export function ProcessingPill({ file }) {
  const info = processingInfo(file);
  if (!info.label) return null;
  return (
    <span className={`fl-chip fl-chip-status${info.error ? ' is-error' : ''}`}>
      {info.busy ? <span className="fl-spinner fl-spinner-xs" aria-hidden="true" /> : null}
      {info.label}
    </span>
  );
}

export function FolderChips({ file, onOpenFolder, max = 2 }) {
  const folders = Array.isArray(file?.folders) ? file.folders : [];
  if (!folders.length) return <span className="fl-chip fl-chip-muted">Unfiled</span>;
  const shown = folders.slice(0, max);
  return (
    <>
      {shown.map((folder, i) => {
        const label = folder.name || folder.path;
        const key = folder.folder_id || `${folder.path}-${i}`;
        if (folder.folder_id && onOpenFolder) {
          return (
            <button
              key={key}
              type="button"
              className="fl-chip fl-chip-folder"
              title={folder.path || label}
              onClick={(e) => { e.stopPropagation(); onOpenFolder(folder.folder_id); }}
            >
              <IoFolderOutline size={11} aria-hidden="true" />{label}
            </button>
          );
        }
        return (
          <span key={key} className="fl-chip fl-chip-folder" title={folder.path || label}>
            <IoFolderOutline size={11} aria-hidden="true" />{label}
          </span>
        );
      })}
      {folders.length > max ? <span className="fl-chip fl-chip-muted">+{folders.length - max}</span> : null}
    </>
  );
}

/**
 * A server-paged, sortable, filterable file list (All / groups / folder /
 * Unfiled / search). `params` are the fixed filters for the view
 * ({ q, sourceGroup, folderId, unfiled }); sort and kind are local.
 *   suggestions:   { busy: { [fileId]: 'accept'|'dismiss' }, onAccept, onDismiss }
 *                  shows Atlas's folder suggestion on unfiled rows
 *   onItemsChange: ({ items, status }) with the rows as shown (local edits applied)
 *   headerExtra:   a node, or ({ items, status }) => node
 */
export default function FileList({
  client,
  params,
  refreshKey,
  overrides,
  title,
  subtitle,
  headerExtra,
  emptyTitle = 'No files here yet',
  emptyHint,
  activeFileId,
  isMobile,
  onOpenFile,
  onOpenFolder,
  onMoveFiles,
  onDeleteFiles,
  onDragFilesStart,
  onDragFilesEnd,
  onFreshFiles,
  suggestions,
  onItemsChange,
}) {
  const [sort, setSort] = useState('recent');
  const [kind, setKind] = useState('all');
  const [state, setState] = useState({ status: 'loading', items: [], total: null, hasMore: false, nextOffset: 0, error: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const requestRef = useRef(0);
  const loadedKeyRef = useRef(null);
  const paramsKey = JSON.stringify(params || {});
  // Unfiled: a row that a local move put into a folder has left this view.
  const hideFiled = Boolean(params?.unfiled);
  const queryKey = `${paramsKey}|${sort}|${kind}`;

  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;
  const onFreshFilesRef = useRef(onFreshFiles);
  onFreshFilesRef.current = onFreshFiles;

  // dropIds: loaded rows that were deleted here since they were fetched. The
  // server no longer counts them, so they leave the list when the next page lands.
  const load = useCallback(async ({ append = false, offset = 0, dropIds = null } = {}) => {
    const requestId = ++requestRef.current;
    if (append) {
      setLoadingMore(true);
    } else {
      // A new first-page load supersedes any "Load more" still in flight.
      setLoadingMore(false);
      // A refresh of the same query keeps the rows on screen; a new query shows the skeleton.
      setState((prev) => (loadedKeyRef.current?.client === client && loadedKeyRef.current?.queryKey === queryKey && prev.items.length
        ? { ...prev, error: null }
        : { status: 'loading', items: [], total: null, hasMore: false, nextOffset: 0, error: null }));
    }
    try {
      const result = await client.listFiles({ ...JSON.parse(paramsKey), sort, kind, offset, limit: PAGE_SIZE });
      if (requestId !== requestRef.current) return;
      loadedKeyRef.current = { client, queryKey };
      const consumed = result.consumed != null ? result.consumed : result.files.length;
      setState((prev) => {
        const kept = dropIds ? prev.items.filter((p) => !dropIds.has(p.file_id)) : prev.items;
        const items = append ? [...kept, ...result.files.filter((f) => !kept.some((p) => p.file_id === f.file_id))] : result.files;
        return {
          status: 'ready',
          items,
          total: result.total,
          hasMore: Boolean(result.hasMore),
          nextOffset: offset + consumed,
          error: null,
        };
      });
      if (result.fetchSeq != null) onFreshFilesRef.current?.(result.files.map((f) => f.file_id), result.fetchSeq);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      if (append) {
        setState((prev) => ({ ...prev, loadMoreError: err?.message || 'Could not load more files.' }));
      } else {
        setState({ status: 'error', items: [], total: null, hasMore: false, nextOffset: 0, error: err?.message || 'Could not load files.' });
      }
    } finally {
      // Always clear for an append, even one superseded by a newer load.
      if (append) setLoadingMore(false);
    }
  }, [client, kind, paramsKey, queryKey, sort]);

  useEffect(() => {
    setSelected(new Set());
    load();
  }, [load, refreshKey]);

  const items = useMemo(() => {
    const rows = applyOverrides(state.items, overrides);
    return hideFiled ? rows.filter((row) => !(row.folders && row.folders.length)) : rows;
  }, [hideFiled, overrides, state.items]);
  const removedCount = state.items.length - items.length;
  const total = state.total != null ? Math.max(0, state.total - removedCount) : null;

  const onItemsChangeRef = useRef(onItemsChange);
  onItemsChangeRef.current = onItemsChange;
  useEffect(() => {
    onItemsChangeRef.current?.({ items, status: state.status });
  }, [items, state.status]);

  const toggleSelected = (fileId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId); else next.add(fileId);
      return next;
    });
  };
  const visibleSelected = items.filter((f) => selected.has(f.file_id));
  const allSelected = items.length > 0 && visibleSelected.length === items.length;

  const handleDragStart = (event, file) => {
    if (!onDragFilesStart) return;
    onDragFilesStart(event, selected.has(file.file_id) ? visibleSelected : [file]);
  };

  const filterLabel = KIND_FILTERS.find((k) => k.id === kind)?.label;
  const countLabel = state.status === 'ready'
    ? (total != null ? pluralize(total, 'file') : pluralize(items.length, 'file'))
    : '';

  const headerNode = typeof headerExtra === 'function' ? headerExtra({ items, status: state.status }) : headerExtra;

  return (
    <section className="fl-list" aria-label={title || 'Files'}>
      {(title || subtitle || headerNode) ? (
        <div className="fl-list-head">
          <div className="fl-list-heading">
            {title ? <h3 className="fl-view-title">{title}</h3> : null}
            {subtitle ? <p className="fl-view-subtitle">{subtitle}</p> : null}
          </div>
          {headerNode}
        </div>
      ) : null}

      <div className="fl-list-toolbar">
        <div className="fl-kind-chips" role="group" aria-label="Filter by type">
          <button type="button" className={`fl-filter-chip${kind === 'all' ? ' is-on' : ''}`} aria-pressed={kind === 'all'} onClick={() => setKind('all')}>
            All types
          </button>
          {KIND_FILTERS.map((k) => (
            <button
              key={k.id}
              type="button"
              className={`fl-filter-chip${kind === k.id ? ' is-on' : ''}`}
              aria-pressed={kind === k.id}
              onClick={() => setKind(kind === k.id ? 'all' : k.id)}
            >
              {k.label}
            </button>
          ))}
        </div>
        <div className="fl-list-toolbar-end">
          {countLabel ? <span className="fl-count" aria-live="polite">{countLabel}</span> : null}
          <label className="fl-sort">
            <span>Sort</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort files">
              {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      {visibleSelected.length > 0 ? (
        <div className="fl-bulk-bar" role="region" aria-label="Selected files">
          <span className="fl-bulk-count">{visibleSelected.length} selected</span>
          {onMoveFiles ? (
            <button type="button" className="fl-btn fl-btn-sm" onClick={() => onMoveFiles(visibleSelected)}>
              <IoReturnUpForwardOutline size={14} aria-hidden="true" /> Move…
            </button>
          ) : null}
          {onDeleteFiles ? (
            <button type="button" className="fl-btn fl-btn-sm fl-btn-danger-ghost" onClick={() => onDeleteFiles(visibleSelected)}>
              <IoTrashOutline size={14} aria-hidden="true" /> Delete
            </button>
          ) : null}
          <button type="button" className="fl-btn fl-btn-sm fl-btn-ghost" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      ) : null}

      {state.status === 'loading' ? (
        <div className="fl-skeleton-list" aria-busy="true" aria-label="Loading files">
          {[0, 1, 2, 3].map((i) => <div key={i} className="fl-skeleton-row" />)}
        </div>
      ) : state.status === 'error' ? (
        <div className="fl-inline-error" role="alert">
          <IoAlertCircleOutline size={16} aria-hidden="true" />
          <span>{state.error}</span>
          <button type="button" className="fl-link-btn" onClick={() => load()}>Retry</button>
        </div>
      ) : items.length === 0 ? (
        <div className="fl-empty">
          <strong>{kind !== 'all' ? `No ${filterLabel || 'matching'} files here` : emptyTitle}</strong>
          {kind !== 'all' ? (
            <button type="button" className="fl-link-btn" onClick={() => setKind('all')}>Show all types</button>
          ) : emptyHint ? <span>{emptyHint}</span> : null}
        </div>
      ) : (
        <>
          <div className="fl-list-columns" aria-hidden={isMobile ? 'true' : undefined}>
            <label className="fl-check">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((f) => f.file_id)))}
                aria-label="Select all files shown"
              />
            </label>
            <span>Name</span>
            <span className="fl-col-size">Size</span>
            <span className="fl-col-date">Added</span>
          </div>
          <ul className="fl-rows">
            {items.map((file) => {
              const isSelected = selected.has(file.file_id);
              return (
                <li
                  key={file.file_id}
                  className={`fl-row${isSelected ? ' is-selected' : ''}${activeFileId === file.file_id ? ' is-active' : ''}`}
                  draggable={!isMobile && Boolean(onDragFilesStart)}
                  onDragStart={(e) => handleDragStart(e, file)}
                  onDragEnd={onDragFilesEnd}
                  onClick={() => onOpenFile(file)}
                  data-file-id={file.file_id}
                >
                  <label className="fl-check" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelected(file.file_id)}
                      aria-label={`Select ${file.filename}`}
                    />
                  </label>
                  <span className={`fl-row-icon fl-kind-${file.kind}`}><FileKindIcon file={file} size={18} /></span>
                  <div className="fl-row-main">
                    <div className="fl-row-title">
                      <button type="button" className="fl-row-name" title={file.filename}>{file.filename}</button>
                      <VersionBadge file={file} />
                      <ProcessingPill file={file} />
                    </div>
                    {file.condensed_summary ? <div className="fl-row-summary" title={file.condensed_summary}>{file.condensed_summary}</div> : null}
                    {suggestions ? (
                      <FolderSuggestion
                        file={file}
                        busy={suggestions.busy?.[file.file_id]}
                        onAccept={suggestions.onAccept}
                        onDismiss={suggestions.onDismiss}
                      />
                    ) : null}
                    <div className="fl-row-meta">
                      <FolderChips file={file} onOpenFolder={onOpenFolder} />
                      <span className="fl-chip fl-chip-source">{SOURCE_GROUP_CHIPS[file.source_group] || 'Other'}</span>
                      <span className="fl-row-meta-mobile">{[formatBytes(file.size_bytes), formatDate(file.created_at)].filter(Boolean).join(' · ')}</span>
                    </div>
                  </div>
                  <span className="fl-col-size">{formatBytes(file.size_bytes)}</span>
                  <span className="fl-col-date">{formatDate(file.created_at)}</span>
                </li>
              );
            })}
          </ul>
          {state.loadMoreError ? (
            <div className="fl-inline-error" role="alert">
              <IoAlertCircleOutline size={16} aria-hidden="true" />
              <span>{state.loadMoreError}</span>
            </div>
          ) : null}
          {state.hasMore ? (
            <div className="fl-load-more">
              <button
                type="button"
                className="fl-btn"
                disabled={loadingMore}
                onClick={() => {
                  // Rows deleted (or, in Unfiled, filed) here since they were fetched.
                  const gone = state.items.filter((row) => {
                    const shown = applyOverride(row, overridesRef.current);
                    return !shown || (hideFiled && shown.folders && shown.folders.length > 0);
                  });
                  const dropIds = new Set(gone.map((row) => row.file_id));
                  setState((prev) => ({ ...prev, loadMoreError: null }));
                  load({ append: true, offset: Math.max(0, state.nextOffset - dropIds.size), dropIds });
                }}
              >
                {loadingMore ? <span className="fl-spinner" aria-hidden="true" /> : null}
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
              {total != null ? <span className="fl-count">Showing {items.length} of {total}</span> : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
